import { useState } from 'react';
import { AlertTriangle, Eye } from 'lucide-react';
import { useAmazonAccounts, useDeleteAmazonAccount, useInitiateAmazonAuth } from '../../features/amazon/hooks';
import { apiClient } from '../../services/apiClient';
import { useUser } from '../../features/auth/useUser';
import type { AmazonSellerAccount, MarketplaceCode } from '../../features/amazon/types';
import { Button } from '../../components/common/Button';
import { CreateButton } from '../../components/common/CreateButton';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import { BackButton } from '../../components/common/BackButton';
import { Label } from '../../components/common/Label';
import type { LabelVariant } from '../../components/common/Label';
import { NewConnectionModal } from '../connections/NewConnectionModal';
import '../../components/common/CommonTable.css';
import '../../components/common/CommonFilters.css';
import '../products/ProductsPageHeader.css';
import './AmazonAuthPage.css';

export default function AmazonAuthPage() {
  const toast = useToast();
  const { confirm, dialog } = useConfirmDialog();
  const { isAdmin, isPlatformOrg } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMarketplace, setSelectedMarketplace] = useState<MarketplaceCode>('JP');
  const [refreshToken, setRefreshToken] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [devMarketplace, setDevMarketplace] = useState<MarketplaceCode>('JP');
  const [isDevAuthLoading, setIsDevAuthLoading] = useState(false);
  const [devAuthError, setDevAuthError] = useState<string | null>(null);

  const { data: accounts = [], isLoading, error, refetch } = useAmazonAccounts();
  const deleteAccountMutation = useDeleteAmazonAccount();
  const initiateAuthMutation = useInitiateAmazonAuth();

  const handleConnect = async () => {
    try {
      await initiateAuthMutation.mutateAsync(selectedMarketplace);
    } catch (err) {
      console.error('Failed to initiate Amazon auth:', err);
      toast.error('Amazon認証の開始に失敗しました');
    }
  };

  const handleReconnect = async (account: AmazonSellerAccount) => {
    try {
      await initiateAuthMutation.mutateAsync(account.marketplace_country_code as MarketplaceCode);
    } catch (err) {
      console.error('Failed to reconnect Amazon account:', err);
      toast.error('Amazon再認証の開始に失敗しました');
    }
  };

  const handleDisconnect = async (account: AmazonSellerAccount) => {
    const ok = await confirm({
      title: `Amazon Seller Account (${account.seller_id}) の接続を解除しますか？`,
      confirmLabel: '解除する',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await deleteAccountMutation.mutateAsync(account.id);
      toast.success('アカウントの接続を解除しました');
    } catch (err) {
      console.error('Failed to delete Amazon account:', err);
      toast.error('アカウントの削除に失敗しました');
    }
  };

  const handleDevAuth = async () => {
    if (!refreshToken.trim()) {
      setDevAuthError('リフレッシュトークンを入力してください');
      return;
    }

    if (!sellerId.trim()) {
      setDevAuthError('Seller IDを入力してください');
      return;
    }

    setIsDevAuthLoading(true);
    setDevAuthError(null);

    try {
      await apiClient.post('/amazon/accounts/dev-auth', {
        refresh_token: refreshToken,
        seller_id: sellerId,
        marketplace: devMarketplace,
      });

      toast.success('認証に成功しました');
      setRefreshToken('');
      setSellerId('');
      refetch();
    } catch (err: any) {
      console.error('Failed to dev-auth:', err);
      setDevAuthError(err.response?.data?.error || err.message || '認証に失敗しました');
    } finally {
      setIsDevAuthLoading(false);
    }
  };

  const getMarketplaceName = (code: string): string => {
    const names: Record<string, string> = {
      JP: '日本',
      US: 'アメリカ',
      GB: 'イギリス',
      DE: 'ドイツ',
      FR: 'フランス',
      IT: 'イタリア',
      ES: 'スペイン',
      CA: 'カナダ',
      MX: 'メキシコ',
    };
    return names[code] || code;
  };

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, LabelVariant> = {
      active: 'green',
      inactive: 'red',
      expired: 'red',
      unknown: 'gray',
    };
    const statusLabels: Record<string, string> = {
      active: '有効',
      inactive: '無効',
      expired: '期限切れ',
      unknown: '不明',
    };

    return (
      <Label variant={statusVariants[status] || 'gray'}>
        {statusLabels[status] || status}
      </Label>
    );
  };

  return (
    <div className="page admin-page">
      <div className="header-actions">
        <BackButton to="/connections" />
      </div>

      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">Amazon Seller 連携</h1>
          <p className="page-description">
            Amazon Seller Centralアカウントを接続して、商品画像の取得・アップロードを行います。
          </p>
        </div>
      </header>

      {!isAdmin && (
        <div className="readonly-banner" role="status">
          <span className="readonly-banner-icon">
            <Eye size={18} aria-hidden="true" />
          </span>
          <span>閲覧のみ可能です。連携・解除等の操作には管理者権限が必要です。</span>
        </div>
      )}

      {/* 接続済みアカウント一覧 */}
      <section className="accounts-section">
        <div className="accounts-section-head">
          <h2 className="section-title">接続済みアカウント</h2>
          {isAdmin && (
            <CreateButton onClick={() => setIsModalOpen(true)}>新規接続</CreateButton>
          )}
        </div>

        {isLoading && <div className="admin-loading">読み込み中...</div>}

        {error && (
          <div className="admin-error">
            アカウント情報の取得に失敗しました: {error.message}
          </div>
        )}

        {!isLoading && !error && accounts.length === 0 && (
          <div className="admin-empty-state">
            <div className="admin-empty-state-title">接続済みのAmazon Seller Accountはありません</div>
            <div className="admin-empty-state-description">
              まずは上部の「接続」ボタンからアカウントを接続してください。
            </div>
          </div>
        )}

        {!isLoading && accounts.length > 0 && (
          <div className="table-container">
            <table className="amazon-accounts-table common-table">
              <thead>
                <tr>
                  <th className="col-seller-id">Seller ID</th>
                  <th className="col-marketplace">マーケットプレイス</th>
                  <th className="col-status">ステータス</th>
                  <th className="col-created-at">接続日時</th>
                  <th className="col-updated-at">最終更新</th>
                  {isAdmin && <th className="col-actions">操作</th>}
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id}>
                    <td className="col-seller-id">
                      <code>{account.seller_id}</code>
                    </td>
                    <td className="col-marketplace">
                      {getMarketplaceName(account.marketplace_country_code)} ({account.marketplace_country_code})
                    </td>
                    <td className="col-status">
                      {getStatusBadge(account.status)}
                    </td>
                    <td className="col-created-at">
                      {new Date(account.created_at).toLocaleString('ja-JP')}
                    </td>
                    <td className="col-updated-at">
                      {account.updated_at ? new Date(account.updated_at).toLocaleString('ja-JP') : '—'}
                    </td>
                    {isAdmin && (
                      <td className="col-actions">
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button
                            onClick={() => handleReconnect(account)}
                            disabled={initiateAuthMutation.isPending}
                            variant="primary"
                            size="sm"
                          >
                            {initiateAuthMutation.isPending ? '認証中...' : '更新'}
                          </Button>
                          <Button
                            onClick={() => void handleDisconnect(account)}
                            disabled={deleteAccountMutation.isPending}
                            variant="danger"
                            size="sm"
                          >
                            {deleteAccountMutation.isPending ? '削除中...' : '接続解除'}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 開発環境用：リフレッシュトークン直接登録（lightmedia組織のみ） */}
      {isAdmin && isPlatformOrg && (
        <section className="auth-section dev-auth-section">
          <details className="dev-auth-details">
            <summary className="dev-auth-summary">
              開発環境用：リフレッシュトークンで登録
            </summary>
            <div className="dev-auth-content">
              <div className="dev-auth-info">
                <p>
                  ローカル開発環境ではHTTPSが使えないため、OAuth認証が利用できません。
                  <a
                    href="https://solutionproviderportal.amazon.com/home"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link"
                  >
                    Amazon Solution Provider Portal
                  </a>
                  から取得したリフレッシュトークンとSeller IDを直接入力して認証できます。
                </p>
                <p>
                  Seller IDはAmazon Seller Centralの「設定」→「アカウント情報」から確認できます（例：A1234567890ABC）。
                </p>
                <p className="warning-text" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={16} aria-hidden="true" />
                  本番環境では必ず上記のOAuth認証を使用してください
                </p>
              </div>
              <div className="connect-form">
                <div className="form-group">
                  <label htmlFor="dev-marketplace">マーケットプレイス:</label>
                  <select
                    id="dev-marketplace"
                    value={devMarketplace}
                    onChange={(e) => setDevMarketplace(e.target.value as MarketplaceCode)}
                    className="form-select"
                  >
                    <option value="JP">日本 (amazon.co.jp)</option>
                    <option value="US">アメリカ (amazon.com)</option>
                    <option value="GB">イギリス (amazon.co.uk)</option>
                    <option value="DE">ドイツ (amazon.de)</option>
                    <option value="FR">フランス (amazon.fr)</option>
                    <option value="IT">イタリア (amazon.it)</option>
                    <option value="ES">スペイン (amazon.es)</option>
                    <option value="CA">カナダ (amazon.ca)</option>
                    <option value="MX">メキシコ (amazon.com.mx)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="seller-id">Seller ID:</label>
                  <input
                    type="text"
                    id="seller-id"
                    value={sellerId}
                    onChange={(e) => setSellerId(e.target.value)}
                    placeholder="A1234567890ABC"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="refresh-token">リフレッシュトークン:</label>
                  <textarea
                    id="refresh-token"
                    value={refreshToken}
                    onChange={(e) => setRefreshToken(e.target.value)}
                    placeholder="Atzr|..."
                    className="form-textarea"
                    rows={4}
                  />
                </div>

                <Button
                  onClick={handleDevAuth}
                  disabled={isDevAuthLoading}
                  variant="primary"
                  size="md"
                >
                  {isDevAuthLoading ? '認証中...' : 'リフレッシュトークンで認証'}
                </Button>

                {devAuthError && (
                  <div className="error-message">{devAuthError}</div>
                )}
              </div>
            </div>
          </details>
        </section>
      )}

      <NewConnectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Amazon Seller アカウントに新規接続"
        description="連携する Amazon マーケットプレイスを選択して接続を開始します。Amazon の同意画面に遷移します。"
      >
        <div className="connect-form">
          <div className="form-group">
            <label htmlFor="marketplace">マーケットプレイス</label>
            <select
              id="marketplace"
              value={selectedMarketplace}
              onChange={(e) => setSelectedMarketplace(e.target.value as MarketplaceCode)}
              className="form-select"
            >
              <option value="JP">日本 (amazon.co.jp)</option>
              <option value="US">アメリカ (amazon.com)</option>
              <option value="GB">イギリス (amazon.co.uk)</option>
              <option value="DE">ドイツ (amazon.de)</option>
              <option value="FR">フランス (amazon.fr)</option>
              <option value="IT">イタリア (amazon.it)</option>
              <option value="ES">スペイン (amazon.es)</option>
              <option value="CA">カナダ (amazon.ca)</option>
              <option value="MX">メキシコ (amazon.com.mx)</option>
            </select>
          </div>

          {initiateAuthMutation.isError && (
            <div className="error-message">
              認証の開始に失敗しました。もう一度お試しください。
            </div>
          )}

          <Button
            onClick={handleConnect}
            disabled={initiateAuthMutation.isPending}
            variant="primary"
            size="md"
          >
            {initiateAuthMutation.isPending ? '認証中...' : '接続'}
          </Button>
        </div>
      </NewConnectionModal>
      {dialog}
    </div>
  );
}
